import { createCipheriv, createDecipheriv, createHash, randomBytes, randomUUID, timingSafeEqual } from "node:crypto";
import fs from "node:fs";
import fsp from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import { DatabaseSync, type SQLInputValue } from "node:sqlite";

export type NoriPaths = {
  homeDir: string;
  stateDir: string;
  configPath: string;
  workspaceDir: string;
  agentsDir: string;
  credentialsDir: string;
  logsDir: string;
  pluginsDir: string;
  sharedDatabasePath: string;
  masterKeyPath: string;
};

export function resolveNoriPaths(options: {
  profile?: string;
  env?: NodeJS.ProcessEnv;
  homeDir?: string;
} = {}): NoriPaths {
  const env = options.env ?? process.env;
  const home = options.homeDir ?? os.homedir();
  const suffix = options.profile ? `-${options.profile}` : "";
  const canonicalHome = path.join(home, `.nori${suffix}`);
  const stateDir = env.NORI_STATE_DIR ?? env.OPENCLAW_STATE_DIR ?? canonicalHome;
  const configPath = env.NORI_CONFIG_PATH ?? env.OPENCLAW_CONFIG_PATH ?? path.join(stateDir, "nori.json");
  return {
    homeDir: canonicalHome,
    stateDir,
    configPath,
    workspaceDir: path.join(stateDir, "workspace"),
    agentsDir: path.join(stateDir, "agents"),
    credentialsDir: path.join(stateDir, "credentials"),
    logsDir: path.join(stateDir, "logs"),
    pluginsDir: path.join(stateDir, "plugins"),
    sharedDatabasePath: path.join(stateDir, "state", "nori.sqlite"),
    masterKeyPath: path.join(stateDir, "credentials", "master.key"),
  };
}

export async function ensurePrivateDirectory(directory: string): Promise<void> {
  await fsp.mkdir(directory, { recursive: true, mode: 0o700 });
  if (process.platform !== "win32") await fsp.chmod(directory, 0o700);
}

export async function ensureNoriDirectories(paths: NoriPaths): Promise<void> {
  await Promise.all([
    ensurePrivateDirectory(paths.stateDir),
    ensurePrivateDirectory(path.dirname(paths.sharedDatabasePath)),
    ensurePrivateDirectory(paths.agentsDir),
    ensurePrivateDirectory(paths.credentialsDir),
    ensurePrivateDirectory(paths.logsDir),
    ensurePrivateDirectory(paths.pluginsDir),
    ensurePrivateDirectory(paths.workspaceDir),
  ]);
}

function decodeMasterKey(encoded: string): Buffer {
  const trimmed = encoded.trim();
  const key = /^[0-9a-f]{64}$/iu.test(trimmed) ? Buffer.from(trimmed, "hex") : Buffer.from(trimmed, "base64");
  if (key.length !== 32) throw new Error("NORI_MASTER_KEY must decode to exactly 32 bytes");
  return key;
}

export class SecretVault {
  #key?: Buffer;

  constructor(
    readonly keyPath: string,
    readonly env: NodeJS.ProcessEnv = process.env,
  ) {}

  async initialize(): Promise<void> {
    if (this.#key) return;
    const fromEnv = this.env.NORI_MASTER_KEY;
    if (fromEnv) {
      this.#key = decodeMasterKey(fromEnv);
      return;
    }
    await ensurePrivateDirectory(path.dirname(this.keyPath));
    try {
      this.#key = decodeMasterKey(await fsp.readFile(this.keyPath, "utf8"));
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
      const key = randomBytes(32);
      await fsp.writeFile(this.keyPath, `${key.toString("base64")}\n`, { mode: 0o600, flag: "wx" });
      this.#key = key;
    }
    if (process.platform !== "win32") await fsp.chmod(this.keyPath, 0o600);
  }

  encrypt(value: string): string {
    if (!this.#key) throw new Error("SecretVault is not initialized");
    const iv = randomBytes(12);
    const cipher = createCipheriv("aes-256-gcm", this.#key, iv);
    const ciphertext = Buffer.concat([cipher.update(value, "utf8"), cipher.final()]);
    const tag = cipher.getAuthTag();
    return `v1.${iv.toString("base64url")}.${tag.toString("base64url")}.${ciphertext.toString("base64url")}`;
  }

  decrypt(envelope: string): string {
    if (!this.#key) throw new Error("SecretVault is not initialized");
    const [version, ivText, tagText, cipherText] = envelope.split(".");
    if (version !== "v1" || !ivText || !tagText || !cipherText) throw new Error("Invalid encrypted secret envelope");
    const decipher = createDecipheriv("aes-256-gcm", this.#key, Buffer.from(ivText, "base64url"));
    decipher.setAuthTag(Buffer.from(tagText, "base64url"));
    return Buffer.concat([
      decipher.update(Buffer.from(cipherText, "base64url")),
      decipher.final(),
    ]).toString("utf8");
  }
}

function openDatabase(databasePath: string): DatabaseSync {
  fs.mkdirSync(path.dirname(databasePath), { recursive: true, mode: 0o700 });
  const database = new DatabaseSync(databasePath);
  database.exec("PRAGMA journal_mode=WAL; PRAGMA foreign_keys=ON; PRAGMA busy_timeout=5000;");
  if (process.platform !== "win32") fs.chmodSync(databasePath, 0o600);
  return database;
}

function migrate(database: DatabaseSync, migrations: readonly string[]): void {
  database.exec("CREATE TABLE IF NOT EXISTS schema_migrations (version INTEGER PRIMARY KEY, applied_at INTEGER NOT NULL)");
  const current = Number(
    (database.prepare("SELECT COALESCE(MAX(version), 0) AS version FROM schema_migrations").get() as { version: number }).version,
  );
  for (let index = current; index < migrations.length; index += 1) {
    database.exec("BEGIN IMMEDIATE");
    try {
      database.exec(migrations[index]!);
      database.prepare("INSERT INTO schema_migrations(version, applied_at) VALUES (?, ?)").run(index + 1, Date.now());
      database.exec("COMMIT");
    } catch (error) {
      database.exec("ROLLBACK");
      throw error;
    }
  }
}

const SHARED_MIGRATIONS = [
  `
    CREATE TABLE pairings (
      channel TEXT NOT NULL,
      account_id TEXT NOT NULL,
      sender_id TEXT NOT NULL,
      code TEXT NOT NULL,
      approved_at INTEGER,
      expires_at INTEGER NOT NULL,
      PRIMARY KEY(channel, account_id, sender_id)
    );
    CREATE UNIQUE INDEX pairings_code_idx ON pairings(code);
    CREATE TABLE deliveries (
      delivery_key TEXT PRIMARY KEY,
      status TEXT NOT NULL,
      attempts INTEGER NOT NULL DEFAULT 0,
      payload_json TEXT NOT NULL,
      updated_at INTEGER NOT NULL
    );
    CREATE TABLE plugins (
      id TEXT PRIMARY KEY,
      package_name TEXT NOT NULL,
      manifest_json TEXT NOT NULL,
      enabled INTEGER NOT NULL DEFAULT 0,
      installed_at INTEGER NOT NULL
    );
    CREATE TABLE approvals (
      id TEXT PRIMARY KEY,
      kind TEXT NOT NULL,
      summary TEXT NOT NULL,
      context_json TEXT NOT NULL,
      status TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      resolved_at INTEGER
    );
  `,
  `
    ALTER TABLE approvals ADD COLUMN approval_key TEXT;
    CREATE UNIQUE INDEX approvals_key_idx ON approvals(approval_key) WHERE approval_key IS NOT NULL;
  `,
  `
    CREATE TABLE cron_jobs (
      id TEXT PRIMARY KEY,
      name TEXT NOT NULL,
      agent_id TEXT NOT NULL,
      session_key TEXT NOT NULL,
      schedule_json TEXT NOT NULL,
      prompt TEXT NOT NULL,
      enabled INTEGER NOT NULL,
      next_run_at INTEGER,
      last_run_at INTEGER,
      last_status TEXT,
      last_error TEXT,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL
    );
    CREATE INDEX cron_jobs_due_idx ON cron_jobs(enabled, next_run_at);
    CREATE TABLE cron_runs (
      id TEXT PRIMARY KEY,
      job_id TEXT NOT NULL,
      task_id TEXT NOT NULL,
      status TEXT NOT NULL,
      output TEXT,
      error TEXT,
      started_at INTEGER NOT NULL,
      finished_at INTEGER
    );
    CREATE INDEX cron_runs_job_idx ON cron_runs(job_id, started_at DESC);
    CREATE TABLE tasks (
      id TEXT PRIMARY KEY,
      kind TEXT NOT NULL,
      status TEXT NOT NULL,
      agent_id TEXT NOT NULL,
      session_key TEXT NOT NULL,
      summary TEXT NOT NULL,
      result_json TEXT,
      error TEXT,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL
    );
    CREATE INDEX tasks_updated_idx ON tasks(updated_at DESC);
  `,
  `
    CREATE TABLE devices (
      device_id TEXT PRIMARY KEY,
      display_name TEXT,
      public_key TEXT NOT NULL,
      roles_json TEXT NOT NULL,
      scopes_json TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL,
      last_connected_at INTEGER
    );
    CREATE TABLE device_pair_requests (
      request_id TEXT PRIMARY KEY,
      device_id TEXT NOT NULL,
      display_name TEXT,
      public_key TEXT NOT NULL,
      role TEXT NOT NULL,
      scopes_json TEXT NOT NULL,
      status TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      expires_at INTEGER NOT NULL,
      resolved_at INTEGER
    );
    CREATE INDEX device_pair_pending_idx ON device_pair_requests(status, created_at DESC);
    CREATE TABLE device_tokens (
      device_id TEXT NOT NULL,
      role TEXT NOT NULL,
      token_hash TEXT NOT NULL,
      scopes_json TEXT NOT NULL,
      issued_at INTEGER NOT NULL,
      revoked_at INTEGER,
      PRIMARY KEY(device_id, role),
      FOREIGN KEY(device_id) REFERENCES devices(device_id) ON DELETE CASCADE
    );
  `,
] as const;

export type CronSchedule =
  | { kind: "at"; atMs: number }
  | { kind: "every"; everyMs: number }
  | { kind: "cron"; expr: string; tz?: "UTC" };

export type StoredCronJob = {
  id: string;
  name: string;
  agentId: string;
  sessionKey: string;
  schedule: CronSchedule;
  prompt: string;
  enabled: boolean;
  nextRunAtMs?: number;
  lastRunAtMs?: number;
  lastStatus?: string;
  lastError?: string;
  createdAt: number;
  updatedAt: number;
};

export type StoredTask = {
  id: string;
  kind: string;
  status: "queued" | "running" | "completed" | "failed" | "cancelled" | "timed_out";
  agentId: string;
  sessionKey: string;
  summary: string;
  result?: unknown;
  error?: string;
  createdAt: number;
  updatedAt: number;
};

function mapCronJob(row: Record<string, unknown>): StoredCronJob {
  return {
    id: String(row.id),
    name: String(row.name),
    agentId: String(row.agent_id),
    sessionKey: String(row.session_key),
    schedule: JSON.parse(String(row.schedule_json)) as CronSchedule,
    prompt: String(row.prompt),
    enabled: Boolean(row.enabled),
    ...(row.next_run_at == null ? {} : { nextRunAtMs: Number(row.next_run_at) }),
    ...(row.last_run_at == null ? {} : { lastRunAtMs: Number(row.last_run_at) }),
    ...(row.last_status == null ? {} : { lastStatus: String(row.last_status) }),
    ...(row.last_error == null ? {} : { lastError: String(row.last_error) }),
    createdAt: Number(row.created_at),
    updatedAt: Number(row.updated_at),
  };
}

function mapTask(row: Record<string, unknown>): StoredTask {
  return {
    id: String(row.id),
    kind: String(row.kind),
    status: String(row.status) as StoredTask["status"],
    agentId: String(row.agent_id),
    sessionKey: String(row.session_key),
    summary: String(row.summary),
    ...(row.result_json == null ? {} : { result: JSON.parse(String(row.result_json)) as unknown }),
    ...(row.error == null ? {} : { error: String(row.error) }),
    createdAt: Number(row.created_at),
    updatedAt: Number(row.updated_at),
  };
}

const AGENT_MIGRATIONS = [
  `
    CREATE TABLE sessions (
      session_key TEXT PRIMARY KEY,
      session_id TEXT NOT NULL,
      agent_id TEXT NOT NULL,
      channel TEXT,
      peer_id TEXT,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL,
      input_tokens INTEGER NOT NULL DEFAULT 0,
      output_tokens INTEGER NOT NULL DEFAULT 0
    );
    CREATE TABLE transcript_events (
      id TEXT PRIMARY KEY,
      session_id TEXT NOT NULL,
      sequence INTEGER NOT NULL,
      role TEXT NOT NULL,
      content_json TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      UNIQUE(session_id, sequence)
    );
    CREATE INDEX transcript_session_idx ON transcript_events(session_id, sequence);
    CREATE TABLE credentials (
      provider TEXT NOT NULL,
      profile TEXT NOT NULL,
      encrypted_value TEXT NOT NULL,
      updated_at INTEGER NOT NULL,
      PRIMARY KEY(provider, profile)
    );
    CREATE TABLE memory_entries (
      id TEXT PRIMARY KEY,
      content TEXT NOT NULL,
      metadata_json TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL
    );
    CREATE VIRTUAL TABLE memory_fts USING fts5(id UNINDEXED, content, tokenize='unicode61 remove_diacritics 2');
  `,
  `
    ALTER TABLE sessions ADD COLUMN label TEXT;
    ALTER TABLE sessions ADD COLUMN category TEXT;
    ALTER TABLE sessions ADD COLUMN archived INTEGER NOT NULL DEFAULT 0;
    ALTER TABLE sessions ADD COLUMN pinned INTEGER NOT NULL DEFAULT 0;
    ALTER TABLE sessions ADD COLUMN unread INTEGER NOT NULL DEFAULT 0;
    ALTER TABLE sessions ADD COLUMN model TEXT;
    CREATE INDEX sessions_updated_idx ON sessions(updated_at DESC);
    CREATE INDEX sessions_archived_idx ON sessions(archived, updated_at DESC);
  `,
  `
    ALTER TABLE memory_entries ADD COLUMN embedding_json TEXT;
  `,
] as const;

export type StoredSession = {
  sessionKey: string;
  sessionId: string;
  agentId: string;
  channel?: string;
  peerId?: string;
  createdAt: number;
  updatedAt: number;
  inputTokens: number;
  outputTokens: number;
  label?: string;
  category?: string;
  archived: boolean;
  pinned: boolean;
  unread: boolean;
  model?: string;
};

function mapSession(row: Record<string, unknown>): StoredSession {
  const session: StoredSession = {
    sessionKey: String(row.session_key),
    sessionId: String(row.session_id),
    agentId: String(row.agent_id),
    createdAt: Number(row.created_at),
    updatedAt: Number(row.updated_at),
    inputTokens: Number(row.input_tokens),
    outputTokens: Number(row.output_tokens),
    archived: Boolean(row.archived),
    pinned: Boolean(row.pinned),
    unread: Boolean(row.unread),
  };
  if (row.channel != null) session.channel = String(row.channel);
  if (row.peer_id != null) session.peerId = String(row.peer_id);
  if (row.label != null) session.label = String(row.label);
  if (row.category != null) session.category = String(row.category);
  if (row.model != null) session.model = String(row.model);
  return session;
}

export class AgentStore {
  readonly database: DatabaseSync;

  constructor(
    readonly databasePath: string,
    readonly vault: SecretVault,
  ) {
    this.database = openDatabase(databasePath);
    migrate(this.database, AGENT_MIGRATIONS);
  }

  ensureSession(params: { sessionKey: string; agentId: string; channel?: string; peerId?: string }): StoredSession {
    const existing = this.database.prepare("SELECT * FROM sessions WHERE session_key = ?").get(params.sessionKey) as
      | Record<string, unknown>
      | undefined;
    if (existing) return mapSession(existing);
    const now = Date.now();
    const sessionId = randomUUID();
    this.database
      .prepare(
        "INSERT INTO sessions(session_key, session_id, agent_id, channel, peer_id, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)",
      )
      .run(params.sessionKey, sessionId, params.agentId, params.channel ?? null, params.peerId ?? null, now, now);
    return {
      sessionKey: params.sessionKey,
      sessionId,
      agentId: params.agentId,
      ...(params.channel ? { channel: params.channel } : {}),
      ...(params.peerId ? { peerId: params.peerId } : {}),
      createdAt: now,
      updatedAt: now,
      inputTokens: 0,
      outputTokens: 0,
      archived: false,
      pinned: false,
      unread: false,
    };
  }

  appendTranscript(sessionId: string, role: string, content: unknown): string {
    const id = randomUUID();
    const next = this.database.prepare("SELECT COALESCE(MAX(sequence), 0) + 1 AS sequence FROM transcript_events WHERE session_id = ?").get(
      sessionId,
    ) as { sequence: number };
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database
        .prepare("INSERT INTO transcript_events(id, session_id, sequence, role, content_json, created_at) VALUES (?, ?, ?, ?, ?, ?)")
        .run(id, sessionId, next.sequence, role, JSON.stringify(content), Date.now());
      this.database.prepare("UPDATE sessions SET updated_at = ? WHERE session_id = ?").run(Date.now(), sessionId);
      this.database.exec("COMMIT");
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
    return id;
  }

  history(sessionId: string, limit = 100): Array<{ id: string; role: string; content: unknown; createdAt: number }> {
    const rows = this.database
      .prepare(
        "SELECT id, role, content_json, created_at FROM transcript_events WHERE session_id = ? ORDER BY sequence DESC LIMIT ?",
      )
      .all(sessionId, limit) as Array<Record<string, unknown>>;
    return rows.reverse().map((row) => ({
      id: String(row.id),
      role: String(row.role),
      content: JSON.parse(String(row.content_json)) as unknown,
      createdAt: Number(row.created_at),
    }));
  }

  transcriptCount(sessionId: string): number {
    const row = this.database.prepare("SELECT COUNT(*) AS count FROM transcript_events WHERE session_id = ?").get(sessionId) as { count: number };
    return Number(row.count);
  }

  listSessions(limit = 100): StoredSession[] {
    return (this.database.prepare("SELECT * FROM sessions ORDER BY updated_at DESC LIMIT ?").all(limit) as Array<Record<string, unknown>>).map(
      mapSession,
    );
  }

  listSessionsFiltered(options: {
    limit?: number;
    offset?: number;
    archived?: boolean;
    search?: string;
    activeMinutes?: number;
  } = {}): StoredSession[] {
    const clauses: string[] = ["archived = ?"];
    const values: SQLInputValue[] = [options.archived ? 1 : 0];
    if (options.search?.trim()) {
      clauses.push("(session_key LIKE ? OR session_id LIKE ? OR label LIKE ?)");
      const pattern = `%${options.search.trim().replaceAll("%", "\\%").replaceAll("_", "\\_")}%`;
      values.push(pattern, pattern, pattern);
    }
    if (options.activeMinutes) {
      clauses.push("updated_at >= ?");
      values.push(Date.now() - options.activeMinutes * 60_000);
    }
    values.push(Math.max(1, Math.min(options.limit ?? 100, 1000)), Math.max(0, options.offset ?? 0));
    return (this.database
      .prepare(`SELECT * FROM sessions WHERE ${clauses.join(" AND ")} ORDER BY pinned DESC, updated_at DESC LIMIT ? OFFSET ?`)
      .all(...values) as Array<Record<string, unknown>>).map(mapSession);
  }

  findSession(selector: { key?: string; sessionId?: string; label?: string }): StoredSession | undefined {
    const [column, value] = selector.key
      ? ["session_key", selector.key]
      : selector.sessionId
        ? ["session_id", selector.sessionId]
        : selector.label
          ? ["label", selector.label]
          : [];
    if (!column || !value) return undefined;
    const row = this.database.prepare(`SELECT * FROM sessions WHERE ${column} = ?`).get(value) as Record<string, unknown> | undefined;
    return row ? mapSession(row) : undefined;
  }

  patchSession(sessionKey: string, patch: { label?: string | null; category?: string | null; archived?: boolean; pinned?: boolean; unread?: boolean; model?: string | null }): StoredSession | undefined {
    const assignments: string[] = [];
    const values: SQLInputValue[] = [];
    for (const [property, column] of [["label", "label"], ["category", "category"], ["model", "model"]] as const) {
      if (property in patch) {
        assignments.push(`${column} = ?`);
        values.push(patch[property] ?? null);
      }
    }
    for (const [property, column] of [["archived", "archived"], ["pinned", "pinned"], ["unread", "unread"]] as const) {
      if (property in patch) {
        assignments.push(`${column} = ?`);
        values.push(patch[property] ? 1 : 0);
      }
    }
    if (!assignments.length) return this.findSession({ key: sessionKey });
    assignments.push("updated_at = ?");
    values.push(Date.now(), sessionKey);
    const result = this.database.prepare(`UPDATE sessions SET ${assignments.join(", ")} WHERE session_key = ?`).run(...values);
    return Number(result.changes) > 0 ? this.findSession({ key: sessionKey }) : undefined;
  }

  resetSession(sessionKey: string): StoredSession | undefined {
    const existing = this.findSession({ key: sessionKey });
    if (!existing) return undefined;
    const nextSessionId = randomUUID();
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database.prepare("DELETE FROM transcript_events WHERE session_id = ?").run(existing.sessionId);
      this.database
        .prepare("UPDATE sessions SET session_id = ?, input_tokens = 0, output_tokens = 0, archived = 0, unread = 0, updated_at = ? WHERE session_key = ?")
        .run(nextSessionId, Date.now(), sessionKey);
      this.database.exec("COMMIT");
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
    return this.findSession({ key: sessionKey });
  }

  deleteSession(sessionKey: string, deleteTranscript = true): boolean {
    const existing = this.findSession({ key: sessionKey });
    if (!existing) return false;
    this.database.exec("BEGIN IMMEDIATE");
    try {
      if (deleteTranscript) this.database.prepare("DELETE FROM transcript_events WHERE session_id = ?").run(existing.sessionId);
      this.database.prepare("DELETE FROM sessions WHERE session_key = ?").run(sessionKey);
      this.database.exec("COMMIT");
      return true;
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
  }

  compactSession(sessionKey: string, maxLines = 60): { compacted: boolean; removed: number; kept: number } | undefined {
    const session = this.findSession({ key: sessionKey });
    if (!session) return undefined;
    const rows = this.database
      .prepare("SELECT id, role, content_json, created_at FROM transcript_events WHERE session_id = ? ORDER BY sequence")
      .all(session.sessionId) as Array<Record<string, unknown>>;
    const bounded = Math.max(1, Math.min(maxLines, 1000));
    if (rows.length <= bounded) return { compacted: false, removed: 0, kept: rows.length };
    const kept = rows.slice(-bounded);
    const removed = rows.length - kept.length;
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database.prepare("DELETE FROM transcript_events WHERE session_id = ?").run(session.sessionId);
      const insert = this.database.prepare("INSERT INTO transcript_events(id, session_id, sequence, role, content_json, created_at) VALUES (?, ?, ?, ?, ?, ?)");
      insert.run(randomUUID(), session.sessionId, 1, "system", JSON.stringify(`[Nori compacted ${removed} older transcript events.]`), Date.now());
      kept.forEach((row, index) => insert.run(String(row.id), session.sessionId, index + 2, String(row.role), String(row.content_json), Number(row.created_at)));
      this.database.prepare("UPDATE sessions SET updated_at = ? WHERE session_key = ?").run(Date.now(), sessionKey);
      this.database.exec("COMMIT");
      return { compacted: true, removed, kept: kept.length };
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
  }

  addUsage(sessionId: string, inputTokens: number, outputTokens: number): void {
    this.database
      .prepare("UPDATE sessions SET input_tokens = input_tokens + ?, output_tokens = output_tokens + ?, updated_at = ? WHERE session_id = ?")
      .run(inputTokens, outputTokens, Date.now(), sessionId);
  }

  static localEmbedding(content: string, dimensions = 192): number[] {
    const normalized = `  ${content.normalize("NFKC").toLocaleLowerCase().replace(/\s+/gu, " ").trim()}  `;
    const vector = Array.from({ length: dimensions }, () => 0);
    for (let index = 0; index < Math.max(1, normalized.length - 2); index += 1) {
      const gram = normalized.slice(index, index + 3);
      let hash = 2_166_136_261;
      for (const character of gram) hash = Math.imul(hash ^ character.codePointAt(0)!, 16_777_619) >>> 0;
      vector[hash % dimensions]! += (hash & 1) === 0 ? 1 : -1;
    }
    const magnitude = Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0)) || 1;
    return vector.map((value) => value / magnitude);
  }

  putMemory(content: string, metadata: Record<string, unknown> = {}, embedding = AgentStore.localEmbedding(content)): string {
    const id = randomUUID();
    const now = Date.now();
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database
        .prepare("INSERT INTO memory_entries(id, content, metadata_json, created_at, updated_at, embedding_json) VALUES (?, ?, ?, ?, ?, ?)")
        .run(id, content, JSON.stringify(metadata), now, now, JSON.stringify(embedding));
      this.database.prepare("INSERT INTO memory_fts(id, content) VALUES (?, ?)").run(id, content);
      this.database.exec("COMMIT");
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
    return id;
  }

  searchMemory(query: string, limit = 10): Array<{ id: string; content: string; metadata: Record<string, unknown> }> {
    const terms = query.match(/[\p{L}\p{N}_-]+/gu) ?? [];
    if (terms.length === 0) return [];
    const ftsQuery = terms.map((term) => `"${term.replaceAll('"', '""')}"`).join(" OR ");
    let lexicalIds = new Set<string>();
    try {
      lexicalIds = new Set((this.database.prepare("SELECT id FROM memory_fts WHERE memory_fts MATCH ? LIMIT 100").all(ftsQuery) as Array<{ id: string }>).map((row) => String(row.id)));
    } catch {
      // Vector similarity remains available if SQLite rejects an unusual FTS expression.
    }
    const queryEmbedding = AgentStore.localEmbedding(query);
    const rows = this.database.prepare("SELECT id, content, metadata_json, embedding_json FROM memory_entries ORDER BY updated_at DESC LIMIT 1000").all() as Array<Record<string, unknown>>;
    const ranked = rows.map((row) => {
      let embedding: number[];
      try { embedding = JSON.parse(String(row.embedding_json ?? "[]")) as number[]; }
      catch { embedding = []; }
      const similarity = embedding.length === queryEmbedding.length
        ? embedding.reduce((score, value, index) => score + value * queryEmbedding[index]!, 0)
        : 0;
      return { row, score: similarity + (lexicalIds.has(String(row.id)) ? 1 : 0) };
    }).filter((entry) => entry.score > 0).sort((left, right) => right.score - left.score).slice(0, Math.max(1, Math.min(limit, 100)));
    return ranked.map(({ row }) => ({
      id: String(row.id),
      content: String(row.content),
      metadata: JSON.parse(String(row.metadata_json)) as Record<string, unknown>,
    }));
  }

  reindexMemoryVectors(): number {
    const rows = this.database.prepare("SELECT id, content FROM memory_entries").all() as Array<{ id: string; content: string }>;
    const update = this.database.prepare("UPDATE memory_entries SET embedding_json = ?, updated_at = ? WHERE id = ?");
    this.database.exec("BEGIN IMMEDIATE");
    try {
      for (const row of rows) update.run(JSON.stringify(AgentStore.localEmbedding(row.content)), Date.now(), row.id);
      this.database.exec("COMMIT");
      return rows.length;
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
  }

  memoryStatus(): { entries: number; vectors: number; dimensions: number; engine: string } {
    const row = this.database.prepare("SELECT COUNT(*) AS entries, SUM(CASE WHEN embedding_json IS NOT NULL THEN 1 ELSE 0 END) AS vectors FROM memory_entries").get() as { entries: number; vectors: number };
    return { entries: Number(row.entries), vectors: Number(row.vectors ?? 0), dimensions: 192, engine: "local-hashed-trigram-cosine" };
  }

  setCredential(provider: string, profile: string, value: string): void {
    const encrypted = this.vault.encrypt(value);
    this.database
      .prepare(
        "INSERT INTO credentials(provider, profile, encrypted_value, updated_at) VALUES (?, ?, ?, ?) ON CONFLICT(provider, profile) DO UPDATE SET encrypted_value = excluded.encrypted_value, updated_at = excluded.updated_at",
      )
      .run(provider, profile, encrypted, Date.now());
  }

  getCredential(provider: string, profile = "default"): string | undefined {
    const row = this.database.prepare("SELECT encrypted_value FROM credentials WHERE provider = ? AND profile = ?").get(provider, profile) as
      | { encrypted_value: string }
      | undefined;
    return row ? this.vault.decrypt(row.encrypted_value) : undefined;
  }

  deleteCredential(provider: string, profile = "default"): boolean {
    return Number(this.database.prepare("DELETE FROM credentials WHERE provider = ? AND profile = ?").run(provider, profile).changes) > 0;
  }

  close(): void {
    this.database.close();
  }
}

export class SharedStore {
  readonly database: DatabaseSync;

  constructor(readonly databasePath: string) {
    this.database = openDatabase(databasePath);
    migrate(this.database, SHARED_MIGRATIONS);
  }

  requestPairing(channel: string, accountId: string, senderId: string): string {
    const code = randomBytes(4).toString("hex").toUpperCase();
    this.database
      .prepare(
        "INSERT INTO pairings(channel, account_id, sender_id, code, expires_at) VALUES (?, ?, ?, ?, ?) ON CONFLICT(channel, account_id, sender_id) DO UPDATE SET code = excluded.code, approved_at = NULL, expires_at = excluded.expires_at",
      )
      .run(channel, accountId, senderId, code, Date.now() + 60 * 60 * 1000);
    return code;
  }

  approvePairing(code: string): boolean {
    const result = this.database
      .prepare("UPDATE pairings SET approved_at = ? WHERE code = ? AND expires_at > ?")
      .run(Date.now(), code.toUpperCase(), Date.now());
    return Number(result.changes) > 0;
  }

  isPaired(channel: string, accountId: string, senderId: string): boolean {
    return Boolean(
      this.database
        .prepare("SELECT 1 AS ok FROM pairings WHERE channel = ? AND account_id = ? AND sender_id = ? AND approved_at IS NOT NULL")
        .get(channel, accountId, senderId),
    );
  }

  listPairings(): Array<Record<string, SQLInputValue>> {
    return this.database.prepare("SELECT channel, account_id, sender_id, code, approved_at, expires_at FROM pairings ORDER BY expires_at DESC").all();
  }

  claimDelivery(key: string, payload: unknown): boolean {
    try {
      this.database
        .prepare("INSERT INTO deliveries(delivery_key, status, payload_json, updated_at) VALUES (?, 'claimed', ?, ?)")
        .run(key, JSON.stringify(payload), Date.now());
      return true;
    } catch (error) {
      if (String(error).includes("UNIQUE constraint failed")) return false;
      throw error;
    }
  }

  completeDelivery(key: string): void {
    this.database.prepare("UPDATE deliveries SET status = 'complete', updated_at = ? WHERE delivery_key = ?").run(Date.now(), key);
  }

  savePlugin(packageName: string, manifest: Record<string, unknown>, enabled = false): void {
    const id = String(manifest.id ?? "");
    if (!id) throw new Error("Plugin manifest id is required");
    this.database
      .prepare(
        "INSERT INTO plugins(id, package_name, manifest_json, enabled, installed_at) VALUES (?, ?, ?, ?, ?) ON CONFLICT(id) DO UPDATE SET package_name = excluded.package_name, manifest_json = excluded.manifest_json, enabled = excluded.enabled",
      )
      .run(id, packageName, JSON.stringify(manifest), enabled ? 1 : 0, Date.now());
  }

  setPluginEnabled(id: string, enabled: boolean): boolean {
    return Number(this.database.prepare("UPDATE plugins SET enabled = ? WHERE id = ?").run(enabled ? 1 : 0, id).changes) > 0;
  }

  removePlugin(id: string): boolean {
    return Number(this.database.prepare("DELETE FROM plugins WHERE id = ?").run(id).changes) > 0;
  }

  listPlugins(): Array<{ id: string; packageName: string; manifest: Record<string, unknown>; enabled: boolean; installedAt: number }> {
    return (this.database.prepare("SELECT * FROM plugins ORDER BY id").all() as Array<Record<string, unknown>>).map((row) => ({
      id: String(row.id),
      packageName: String(row.package_name),
      manifest: JSON.parse(String(row.manifest_json)) as Record<string, unknown>,
      enabled: Boolean(row.enabled),
      installedAt: Number(row.installed_at),
    }));
  }

  createApproval(kind: string, summary: string, context: Record<string, unknown>, approvalKey?: string): string {
    if (approvalKey) {
      const existing = this.database.prepare("SELECT id FROM approvals WHERE approval_key = ?").get(approvalKey) as { id: string } | undefined;
      if (existing) return String(existing.id);
    }
    const id = randomUUID();
    this.database
      .prepare("INSERT INTO approvals(id, kind, summary, context_json, status, created_at, approval_key) VALUES (?, ?, ?, ?, 'pending', ?, ?)")
      .run(id, kind, summary, JSON.stringify(context), Date.now(), approvalKey ?? null);
    return id;
  }

  resolveApproval(id: string, decision: "approved" | "denied"): boolean {
    return Number(
      this.database
        .prepare("UPDATE approvals SET status = ?, resolved_at = ? WHERE id = ? AND status = 'pending'")
        .run(decision, Date.now(), id).changes,
    ) > 0;
  }

  getApproval(id: string): Record<string, unknown> | undefined {
    const row = this.database.prepare("SELECT * FROM approvals WHERE id = ?").get(id) as Record<string, unknown> | undefined;
    if (!row) return undefined;
    return {
      id: String(row.id),
      kind: String(row.kind),
      summary: String(row.summary),
      context: JSON.parse(String(row.context_json)) as unknown,
      status: String(row.status),
      ...(row.approval_key == null ? {} : { approvalKey: String(row.approval_key) }),
      createdAt: Number(row.created_at),
      ...(row.resolved_at == null ? {} : { resolvedAt: Number(row.resolved_at) }),
    };
  }

  getApprovalByKey(approvalKey: string): Record<string, unknown> | undefined {
    const row = this.database.prepare("SELECT id FROM approvals WHERE approval_key = ?").get(approvalKey) as { id: string } | undefined;
    return row ? this.getApproval(String(row.id)) : undefined;
  }

  consumeApproval(id: string): boolean {
    return Number(this.database.prepare("UPDATE approvals SET status = 'consumed' WHERE id = ? AND status = 'approved'").run(id).changes) > 0;
  }

  listApprovals(status?: string): Array<Record<string, unknown>> {
    const rows = status
      ? this.database.prepare("SELECT * FROM approvals WHERE status = ? ORDER BY created_at DESC").all(status)
      : this.database.prepare("SELECT * FROM approvals ORDER BY created_at DESC").all();
    return (rows as Array<Record<string, unknown>>).map((row) => ({
      id: String(row.id),
      kind: String(row.kind),
      summary: String(row.summary),
      context: JSON.parse(String(row.context_json)) as unknown,
      status: String(row.status),
      ...(row.approval_key == null ? {} : { approvalKey: String(row.approval_key) }),
      createdAt: Number(row.created_at),
      ...(row.resolved_at == null ? {} : { resolvedAt: Number(row.resolved_at) }),
    }));
  }

  addCronJob(params: {
    id?: string;
    name: string;
    agentId: string;
    sessionKey: string;
    schedule: CronSchedule;
    prompt: string;
    enabled?: boolean;
    nextRunAtMs?: number;
  }): StoredCronJob {
    const id = params.id ?? randomUUID();
    const now = Date.now();
    this.database
      .prepare("INSERT INTO cron_jobs(id, name, agent_id, session_key, schedule_json, prompt, enabled, next_run_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)")
      .run(id, params.name, params.agentId, params.sessionKey, JSON.stringify(params.schedule), params.prompt, params.enabled === false ? 0 : 1, params.nextRunAtMs ?? null, now, now);
    return this.getCronJob(id)!;
  }

  getCronJob(id: string): StoredCronJob | undefined {
    const row = this.database.prepare("SELECT * FROM cron_jobs WHERE id = ?").get(id) as Record<string, unknown> | undefined;
    return row ? mapCronJob(row) : undefined;
  }

  listCronJobs(options: { limit?: number; offset?: number; enabled?: boolean } = {}): StoredCronJob[] {
    const values: SQLInputValue[] = [];
    const where = options.enabled === undefined ? "" : "WHERE enabled = ?";
    if (options.enabled !== undefined) values.push(options.enabled ? 1 : 0);
    values.push(Math.max(1, Math.min(options.limit ?? 100, 1000)), Math.max(0, options.offset ?? 0));
    return (this.database.prepare(`SELECT * FROM cron_jobs ${where} ORDER BY created_at DESC LIMIT ? OFFSET ?`).all(...values) as Array<Record<string, unknown>>).map(mapCronJob);
  }

  updateCronJob(id: string, patch: {
    name?: string;
    agentId?: string;
    sessionKey?: string;
    schedule?: CronSchedule;
    prompt?: string;
    enabled?: boolean;
    nextRunAtMs?: number | null;
  }): StoredCronJob | undefined {
    const assignments: string[] = [];
    const values: SQLInputValue[] = [];
    for (const [property, column] of [["name", "name"], ["agentId", "agent_id"], ["sessionKey", "session_key"], ["prompt", "prompt"]] as const) {
      if (property in patch) {
        assignments.push(`${column} = ?`);
        values.push(patch[property]!);
      }
    }
    if (patch.schedule !== undefined) {
      assignments.push("schedule_json = ?");
      values.push(JSON.stringify(patch.schedule));
    }
    if (patch.enabled !== undefined) {
      assignments.push("enabled = ?");
      values.push(patch.enabled ? 1 : 0);
    }
    if (Object.hasOwn(patch, "nextRunAtMs")) {
      assignments.push("next_run_at = ?");
      values.push(patch.nextRunAtMs ?? null);
    }
    if (!assignments.length) return this.getCronJob(id);
    assignments.push("updated_at = ?");
    values.push(Date.now(), id);
    const result = this.database.prepare(`UPDATE cron_jobs SET ${assignments.join(", ")} WHERE id = ?`).run(...values);
    return Number(result.changes) > 0 ? this.getCronJob(id) : undefined;
  }

  removeCronJob(id: string): boolean {
    return Number(this.database.prepare("DELETE FROM cron_jobs WHERE id = ?").run(id).changes) > 0;
  }

  dueCronJobs(now = Date.now(), limit = 25): StoredCronJob[] {
    return (this.database
      .prepare("SELECT * FROM cron_jobs WHERE enabled = 1 AND next_run_at IS NOT NULL AND next_run_at <= ? ORDER BY next_run_at LIMIT ?")
      .all(now, Math.max(1, Math.min(limit, 100))) as Array<Record<string, unknown>>).map(mapCronJob);
  }

  beginCronRun(params: { jobId: string; taskId: string; nextRunAtMs?: number; disable?: boolean }): { runId: string; startedAt: number } {
    const runId = randomUUID();
    const now = Date.now();
    this.database.exec("BEGIN IMMEDIATE");
    try {
      const changed = this.database
        .prepare("UPDATE cron_jobs SET last_run_at = ?, last_status = 'running', last_error = NULL, next_run_at = ?, enabled = CASE WHEN ? THEN 0 ELSE enabled END, updated_at = ? WHERE id = ?")
        .run(now, params.nextRunAtMs ?? null, params.disable ? 1 : 0, now, params.jobId);
      if (Number(changed.changes) === 0) throw new Error(`Unknown cron job: ${params.jobId}`);
      this.database
        .prepare("INSERT INTO cron_runs(id, job_id, task_id, status, started_at) VALUES (?, ?, ?, 'running', ?)")
        .run(runId, params.jobId, params.taskId, now);
      this.database.exec("COMMIT");
      return { runId, startedAt: now };
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
  }

  finishCronRun(runId: string, status: "completed" | "failed" | "cancelled" | "timed_out", output?: string, error?: string): boolean {
    const run = this.database.prepare("SELECT job_id FROM cron_runs WHERE id = ?").get(runId) as { job_id: string } | undefined;
    if (!run) return false;
    const now = Date.now();
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database.prepare("UPDATE cron_runs SET status = ?, output = ?, error = ?, finished_at = ? WHERE id = ?").run(status, output ?? null, error ?? null, now, runId);
      this.database.prepare("UPDATE cron_jobs SET last_status = ?, last_error = ?, updated_at = ? WHERE id = ?").run(status, error ?? null, now, run.job_id);
      this.database.exec("COMMIT");
      return true;
    } catch (cause) {
      this.database.exec("ROLLBACK");
      throw cause;
    }
  }

  listCronRuns(jobId?: string, limit = 100): Array<Record<string, unknown>> {
    const bounded = Math.max(1, Math.min(limit, 1000));
    const rows = jobId
      ? this.database.prepare("SELECT * FROM cron_runs WHERE job_id = ? ORDER BY started_at DESC LIMIT ?").all(jobId, bounded)
      : this.database.prepare("SELECT * FROM cron_runs ORDER BY started_at DESC LIMIT ?").all(bounded);
    return (rows as Array<Record<string, unknown>>).map((row) => ({
      id: String(row.id),
      jobId: String(row.job_id),
      taskId: String(row.task_id),
      status: String(row.status),
      ...(row.output == null ? {} : { output: String(row.output) }),
      ...(row.error == null ? {} : { error: String(row.error) }),
      startedAt: Number(row.started_at),
      ...(row.finished_at == null ? {} : { finishedAt: Number(row.finished_at) }),
    }));
  }

  saveTask(params: {
    id?: string;
    kind: string;
    status: StoredTask["status"];
    agentId: string;
    sessionKey: string;
    summary: string;
    result?: unknown;
    error?: string;
  }): StoredTask {
    const id = params.id ?? randomUUID();
    const now = Date.now();
    this.database
      .prepare("INSERT INTO tasks(id, kind, status, agent_id, session_key, summary, result_json, error, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?) ON CONFLICT(id) DO UPDATE SET status = excluded.status, result_json = excluded.result_json, error = excluded.error, updated_at = excluded.updated_at")
      .run(id, params.kind, params.status, params.agentId, params.sessionKey, params.summary, params.result === undefined ? null : JSON.stringify(params.result), params.error ?? null, now, now);
    return this.getTask(id)!;
  }

  getTask(id: string): StoredTask | undefined {
    const row = this.database.prepare("SELECT * FROM tasks WHERE id = ?").get(id) as Record<string, unknown> | undefined;
    return row ? mapTask(row) : undefined;
  }

  listTasks(options: { status?: StoredTask["status"]; limit?: number; offset?: number } = {}): StoredTask[] {
    const values: SQLInputValue[] = [];
    const where = options.status ? "WHERE status = ?" : "";
    if (options.status) values.push(options.status);
    values.push(Math.max(1, Math.min(options.limit ?? 100, 1000)), Math.max(0, options.offset ?? 0));
    return (this.database.prepare(`SELECT * FROM tasks ${where} ORDER BY updated_at DESC LIMIT ? OFFSET ?`).all(...values) as Array<Record<string, unknown>>).map(mapTask);
  }

  recoverRunningTasks(): number {
    const now = Date.now();
    const result = this.database
      .prepare("UPDATE tasks SET status = 'failed', error = 'Gateway restarted before task completion', updated_at = ? WHERE status IN ('queued', 'running')")
      .run(now);
    this.database
      .prepare("UPDATE cron_runs SET status = 'failed', error = 'Gateway restarted before task completion', finished_at = ? WHERE status = 'running'")
      .run(now);
    this.database
      .prepare("UPDATE cron_jobs SET last_status = 'failed', last_error = 'Gateway restarted before task completion', updated_at = ? WHERE last_status = 'running'")
      .run(now);
    return Number(result.changes);
  }

  requestDevicePairing(params: { deviceId: string; displayName?: string; publicKey: string; role: string; scopes: string[] }): string {
    const existing = this.database
      .prepare("SELECT request_id FROM device_pair_requests WHERE device_id = ? AND status = 'pending' ORDER BY created_at DESC LIMIT 1")
      .get(params.deviceId) as { request_id: string } | undefined;
    if (existing) {
      this.database.prepare("UPDATE device_pair_requests SET status = 'superseded', resolved_at = ? WHERE request_id = ?").run(Date.now(), existing.request_id);
    }
    const requestId = randomUUID();
    const now = Date.now();
    this.database
      .prepare("INSERT INTO device_pair_requests(request_id, device_id, display_name, public_key, role, scopes_json, status, created_at, expires_at) VALUES (?, ?, ?, ?, ?, ?, 'pending', ?, ?)")
      .run(requestId, params.deviceId, params.displayName ?? null, params.publicKey, params.role, JSON.stringify([...new Set(params.scopes)]), now, now + 10 * 60_000);
    return requestId;
  }

  listDevices(): { pending: Array<Record<string, unknown>>; paired: Array<Record<string, unknown>> } {
    const pending = (this.database.prepare("SELECT * FROM device_pair_requests WHERE status = 'pending' AND expires_at > ? ORDER BY created_at DESC").all(Date.now()) as Array<Record<string, unknown>>).map((row) => ({
      requestId: String(row.request_id),
      deviceId: String(row.device_id),
      ...(row.display_name == null ? {} : { displayName: String(row.display_name) }),
      role: String(row.role),
      scopes: JSON.parse(String(row.scopes_json)) as string[],
      createdAt: Number(row.created_at),
      expiresAt: Number(row.expires_at),
    }));
    const paired = (this.database.prepare("SELECT * FROM devices ORDER BY updated_at DESC").all() as Array<Record<string, unknown>>).map((row) => ({
      deviceId: String(row.device_id),
      ...(row.display_name == null ? {} : { displayName: String(row.display_name) }),
      roles: JSON.parse(String(row.roles_json)) as string[],
      scopes: JSON.parse(String(row.scopes_json)) as string[],
      createdAt: Number(row.created_at),
      updatedAt: Number(row.updated_at),
      ...(row.last_connected_at == null ? {} : { lastConnectedAt: Number(row.last_connected_at) }),
    }));
    return { pending, paired };
  }

  approveDevicePairing(requestId: string): { deviceId: string; role: string; scopes: string[]; token: string } | undefined {
    const row = this.database.prepare("SELECT * FROM device_pair_requests WHERE request_id = ? AND status = 'pending' AND expires_at > ?").get(requestId, Date.now()) as Record<string, unknown> | undefined;
    if (!row) return undefined;
    const deviceId = String(row.device_id);
    const role = String(row.role);
    const scopes = JSON.parse(String(row.scopes_json)) as string[];
    const existing = this.database.prepare("SELECT roles_json, scopes_json FROM devices WHERE device_id = ?").get(deviceId) as { roles_json: string; scopes_json: string } | undefined;
    const roles = [...new Set([...(existing ? JSON.parse(existing.roles_json) as string[] : []), role])];
    const allScopes = [...new Set([...(existing ? JSON.parse(existing.scopes_json) as string[] : []), ...scopes])];
    const token = randomBytes(32).toString("base64url");
    const now = Date.now();
    this.database.exec("BEGIN IMMEDIATE");
    try {
      this.database
        .prepare("INSERT INTO devices(device_id, display_name, public_key, roles_json, scopes_json, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?) ON CONFLICT(device_id) DO UPDATE SET display_name = excluded.display_name, public_key = excluded.public_key, roles_json = excluded.roles_json, scopes_json = excluded.scopes_json, updated_at = excluded.updated_at")
        .run(deviceId, row.display_name == null ? null : String(row.display_name), String(row.public_key), JSON.stringify(roles), JSON.stringify(allScopes), now, now);
      this.database
        .prepare("INSERT INTO device_tokens(device_id, role, token_hash, scopes_json, issued_at, revoked_at) VALUES (?, ?, ?, ?, ?, NULL) ON CONFLICT(device_id, role) DO UPDATE SET token_hash = excluded.token_hash, scopes_json = excluded.scopes_json, issued_at = excluded.issued_at, revoked_at = NULL")
        .run(deviceId, role, createHash("sha256").update(token).digest("hex"), JSON.stringify(scopes), now);
      this.database.prepare("UPDATE device_pair_requests SET status = 'approved', resolved_at = ? WHERE request_id = ?").run(now, requestId);
      this.database.exec("COMMIT");
      return { deviceId, role, scopes, token };
    } catch (error) {
      this.database.exec("ROLLBACK");
      throw error;
    }
  }

  rejectDevicePairing(requestId: string): boolean {
    return Number(this.database.prepare("UPDATE device_pair_requests SET status = 'rejected', resolved_at = ? WHERE request_id = ? AND status = 'pending'").run(Date.now(), requestId).changes) > 0;
  }

  authenticateDeviceToken(token: string): { deviceId: string; role: string; scopes: string[]; publicKey: string } | undefined {
    const hash = createHash("sha256").update(token).digest("hex");
    const rows = this.database
      .prepare("SELECT t.device_id, t.role, t.scopes_json, t.token_hash, d.public_key FROM device_tokens t JOIN devices d ON d.device_id = t.device_id WHERE t.revoked_at IS NULL")
      .all() as Array<Record<string, unknown>>;
    const expected = Buffer.from(hash, "hex");
    const row = rows.find((candidate) => {
      const actual = Buffer.from(String(candidate.token_hash), "hex");
      return actual.length === expected.length && timingSafeEqual(actual, expected);
    });
    return row ? { deviceId: String(row.device_id), role: String(row.role), scopes: JSON.parse(String(row.scopes_json)) as string[], publicKey: String(row.public_key) } : undefined;
  }

  touchDevice(deviceId: string): void {
    this.database.prepare("UPDATE devices SET last_connected_at = ?, updated_at = ? WHERE device_id = ?").run(Date.now(), Date.now(), deviceId);
  }

  rotateDeviceToken(deviceId: string, role: string, scopes?: string[]): { token: string; scopes: string[] } | undefined {
    const row = this.database.prepare("SELECT scopes_json FROM device_tokens WHERE device_id = ? AND role = ? AND revoked_at IS NULL").get(deviceId, role) as { scopes_json: string } | undefined;
    if (!row) return undefined;
    const nextScopes = scopes ?? JSON.parse(row.scopes_json) as string[];
    const token = randomBytes(32).toString("base64url");
    this.database.prepare("UPDATE device_tokens SET token_hash = ?, scopes_json = ?, issued_at = ?, revoked_at = NULL WHERE device_id = ? AND role = ?").run(createHash("sha256").update(token).digest("hex"), JSON.stringify([...new Set(nextScopes)]), Date.now(), deviceId, role);
    return { token, scopes: nextScopes };
  }

  revokeDeviceToken(deviceId: string, role: string): boolean {
    return Number(this.database.prepare("UPDATE device_tokens SET revoked_at = ? WHERE device_id = ? AND role = ? AND revoked_at IS NULL").run(Date.now(), deviceId, role).changes) > 0;
  }

  removeDevice(deviceId: string): boolean {
    return Number(this.database.prepare("DELETE FROM devices WHERE device_id = ?").run(deviceId).changes) > 0;
  }

  renameDevice(deviceId: string, displayName: string): boolean {
    return Number(this.database.prepare("UPDATE devices SET display_name = ?, updated_at = ? WHERE device_id = ?").run(displayName, Date.now(), deviceId).changes) > 0;
  }

  clearDevices(includePending = false): { devices: number; pending: number } {
    const devices = Number(this.database.prepare("DELETE FROM devices").run().changes);
    const pending = includePending ? Number(this.database.prepare("UPDATE device_pair_requests SET status = 'rejected', resolved_at = ? WHERE status = 'pending'").run(Date.now()).changes) : 0;
    return { devices, pending };
  }

  close(): void {
    this.database.close();
  }
}

export function agentDatabasePath(paths: NoriPaths, agentId: string): string {
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/u.test(agentId)) throw new Error(`Invalid agent id: ${agentId}`);
  return path.join(paths.agentsDir, agentId, "agent", "nori-agent.sqlite");
}
